import { Injectable } from '@angular/core';

export type AuthStorageMode = 'local' | 'session';

export interface StoredAuthToken {
    token: string;
    refreshToken?: string;
    expiresAt: string;
    auxiliaryTime: number;
    storageMode: AuthStorageMode;
}

export interface AuthTokenResponse {
    token?: string;
    refreshToken?: string;
    expiresAt?: string;
    auxiliaryTime?: number;
}

@Injectable({
    providedIn: 'root',
})
export class AuthTokenStorageService {
    private readonly accessTokenKey = 'tokenValor';
    private readonly refreshTokenKey = 'tokenAtualizacao';
    private readonly expiresAtKey = 'expiraEm';
    private readonly auxiliaryTimeKey = 'tempoAuxiliar';
    private readonly isAuthenticatedKey = 'isAuthenticated';
    private readonly isAuthenticationCompleteKey = 'isAuthenticationComplete';

    private readonly legacyAccessTokenKey = 'token';
    private readonly legacyRefreshTokenKey = 'refreshToken';
    private readonly legacyExpiresAtKey = 'expiresAt';
    private readonly legacyAuxiliaryTimeKey = 'auxTime';

    readStoredToken(): StoredAuthToken | null {
        const fromSession = this.readFromSessionCookies();
        if (fromSession) {
            return fromSession;
        }

        return this.readFromLocalStorage();
    }

    getAccessToken(): string {
        return (
            this.readSessionCookie(this.accessTokenKey) ??
            localStorage.getItem(this.accessTokenKey) ??
            ''
        );
    }

    isAuthenticated(): boolean {
        return this.readAuthFlag(this.isAuthenticatedKey) === 'true';
    }

    isAuthenticationComplete(): boolean {
        return this.readAuthFlag(this.isAuthenticationCompleteKey) === 'true';
    }

    saveToken(tokenResponse: AuthTokenResponse, storageMode: AuthStorageMode): void {
        this.clearTokens();
        this.clearAuthFlags();

        const token = tokenResponse.token ?? '';
        const refreshToken = tokenResponse.refreshToken ?? '';
        const expiresAtValue = tokenResponse.expiresAt ?? new Date().toISOString();
        const auxiliaryTime = tokenResponse.auxiliaryTime ?? 0;
        const expiresAtIso = new Date(expiresAtValue).toISOString();

        if (storageMode === 'local') {
            localStorage.setItem(this.accessTokenKey, token);
            localStorage.setItem(this.refreshTokenKey, refreshToken);
            localStorage.setItem(this.expiresAtKey, expiresAtIso);
            localStorage.setItem(this.auxiliaryTimeKey, String(auxiliaryTime));

            localStorage.setItem(this.legacyAccessTokenKey, token);
            localStorage.setItem(this.legacyRefreshTokenKey, refreshToken);
            localStorage.setItem(this.legacyExpiresAtKey, expiresAtIso);
            localStorage.setItem(this.legacyAuxiliaryTimeKey, String(auxiliaryTime));

            localStorage.setItem(this.isAuthenticatedKey, 'true');
            localStorage.setItem(this.isAuthenticationCompleteKey, 'true');
            return;
        }

        this.writeSessionCookie(this.accessTokenKey, token);
        this.writeSessionCookie(this.refreshTokenKey, refreshToken);
        this.writeSessionCookie(this.expiresAtKey, expiresAtIso);
        this.writeSessionCookie(this.auxiliaryTimeKey, String(auxiliaryTime));

        this.writeSessionCookie(this.legacyAccessTokenKey, token);
        this.writeSessionCookie(this.legacyRefreshTokenKey, refreshToken);
        this.writeSessionCookie(this.legacyExpiresAtKey, expiresAtIso);
        this.writeSessionCookie(this.legacyAuxiliaryTimeKey, String(auxiliaryTime));

        this.writeSessionCookie(this.isAuthenticatedKey, 'true');
        this.writeSessionCookie(this.isAuthenticationCompleteKey, 'true');
    }

    markAnonymous(): void {
        this.clearTokens();
        this.clearAuthFlags();
        this.writeSessionCookie(this.isAuthenticatedKey, 'false');
        this.writeSessionCookie(this.isAuthenticationCompleteKey, 'true');
    }

    private clearTokens(): void {
        localStorage.removeItem(this.accessTokenKey);
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem(this.expiresAtKey);
        localStorage.removeItem(this.auxiliaryTimeKey);

        localStorage.removeItem(this.legacyAccessTokenKey);
        localStorage.removeItem(this.legacyRefreshTokenKey);
        localStorage.removeItem(this.legacyExpiresAtKey);
        localStorage.removeItem(this.legacyAuxiliaryTimeKey);

        this.clearSessionCookie(this.accessTokenKey);
        this.clearSessionCookie(this.refreshTokenKey);
        this.clearSessionCookie(this.expiresAtKey);
        this.clearSessionCookie(this.auxiliaryTimeKey);

        this.clearSessionCookie(this.legacyAccessTokenKey);
        this.clearSessionCookie(this.legacyRefreshTokenKey);
        this.clearSessionCookie(this.legacyExpiresAtKey);
        this.clearSessionCookie(this.legacyAuxiliaryTimeKey);
    }

    private clearAuthFlags(): void {
        localStorage.removeItem(this.isAuthenticatedKey);
        localStorage.removeItem(this.isAuthenticationCompleteKey);

        this.clearSessionCookie(this.isAuthenticatedKey);
        this.clearSessionCookie(this.isAuthenticationCompleteKey);
    }

    private readAuthFlag(flagKey: string): string | null {
        return this.readSessionCookie(flagKey) ?? localStorage.getItem(flagKey);
    }

    private readFromLocalStorage(): StoredAuthToken | null {
        const token = localStorage.getItem(this.accessTokenKey);
        const refreshToken = localStorage.getItem(this.refreshTokenKey) ?? undefined;

        if (!token) {
            return null;
        }

        return {
            token,
            refreshToken,
            expiresAt: localStorage.getItem(this.expiresAtKey) ?? new Date().toISOString(),
            auxiliaryTime: Number(localStorage.getItem(this.auxiliaryTimeKey) ?? '0'),
            storageMode: 'local',
        };
    }

    private readFromSessionCookies(): StoredAuthToken | null {
        const token = this.readSessionCookie(this.accessTokenKey);
        const refreshToken = this.readSessionCookie(this.refreshTokenKey) ?? undefined;

        if (!token) {
            return null;
        }

        return {
            token,
            refreshToken,
            expiresAt: this.readSessionCookie(this.expiresAtKey) ?? new Date().toISOString(),
            auxiliaryTime: Number(this.readSessionCookie(this.auxiliaryTimeKey) ?? '0'),
            storageMode: 'session',
        };
    }

    private writeSessionCookie(key: string, value: string): void {
        if (typeof document === 'undefined') {
            return;
        }

        document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
    }

    private readSessionCookie(key: string): string | null {
        if (typeof document === 'undefined') {
            return null;
        }

        const encodedKey = `${encodeURIComponent(key)}=`;
        const entries = document.cookie ? document.cookie.split('; ') : [];

        for (const entry of entries) {
            if (!entry.startsWith(encodedKey)) {
                continue;
            }

            return decodeURIComponent(entry.substring(encodedKey.length));
        }

        return null;
    }

    private clearSessionCookie(key: string): void {
        if (typeof document === 'undefined') {
            return;
        }

        document.cookie = `${encodeURIComponent(key)}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
}
